import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/** The query parameter naming the record whose dialog a link opens. */
export const EDIT_PARAM = 'edit';

/**
 * Opens a record's edit dialog from `?edit=<id>` — the change log's way into a
 * screen whose records have no route of their own (Teams, Projekt-Gruppen).
 *
 * `ready` holds it back until everything the dialog is filled from has loaded:
 * a Team's Maßnahmen are read off the projects, and a dialog opened before they
 * arrive would save the team with none. A record that is not (yet) there waits,
 * and opens the moment it arrives.
 *
 * The parameter is dropped once used, replacing the history entry, so closing
 * the dialog does not open it again and Back leads to where the link was.
 */
export function useEditParam(
    records: readonly { id: string }[],
    ready: boolean,
    open: (id: string) => void
) {
    const [searchParams, setSearchParams] = useSearchParams();
    const editId = searchParams.get(EDIT_PARAM);

    // The page's opener closes over its latest state; the effect only has to
    // run when the id or the records change.
    const openRef = useRef(open);
    useEffect(() => {
        openRef.current = open;
    });

    useEffect(() => {
        if (
            !editId ||
            !ready ||
            !records.some((record) => record.id === editId)
        ) {
            return;
        }
        openRef.current(editId);
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);
                next.delete(EDIT_PARAM);
                return next;
            },
            { replace: true }
        );
    }, [editId, ready, records, setSearchParams]);
}
